import { useCallback, useMemo, useState } from "react";
import {
  Campaign,
  DEFAULT_EVENT_TEXTS_CAMPAIGN_NAME,
  DEFAULT_LINK_SUBMISSION_TEXTS_CAMPAIGN_NAME,
  DEFAULT_PRODUCT_TEXTS_CAMPAIGN_NAME,
} from "@markit/common.types";
import { Colors } from "../../../utils/colors";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import filter from "lodash.filter";
import SearchBoxContainer from "../../Containers/SearchBoxContainer";
import EmptyStateButton from "../../Buttons/EmptyStateButton";
import { EmptyStateFlatlist } from "../../EmptyStates/EmptyStateFlatlist";
import StandardListContainer from "../../Containers/StandardListContainer";
import { CampaignConfirmDeleteModal } from "./Items/CampaignConfirmDeleteModal";
import FlatlistLoadMore from "../../FlatlistLoadMore";
import { useLoadAutomationItems } from "../../../hooks/useLoadAutomationItems";
import { isAutomationActive } from "../../../utils/campaignUtils";
import { getEventState } from "../../../redux/slices/eventSlice";
import {
  isDefaultPresetAutomations,
  isDefaultTemplateAutomations,
  NON_SUBSCRIBER_REPLY_CAMPAIGN_NAME,
  SUBSCRIBED_CAMPAIGN_NAME,
} from "@markit/common.utils";
import { AutomationItemPreview } from "../../DisplayItem/CampaignItem/AutomationItemPreview";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import ListTabs from "../../DropdownsAndTabs/ListTabs";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import {
  getCampaignState,
  loadTemplateAutomations,
} from "../../../redux/slices/campaignSlice";

const enum AutomationTabValue {
  ACTIVE = 0,
  INACTIVE = 1,
  TEMPLATES = 2,
  OTHER = 3,
}

type AutomationsListPanelProps = {
  tabValue: number;
};

const AutomationsListPanel = (_props: AutomationsListPanelProps) => {
  const { accountData } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const { campaigns, campaignDetails } = useSelector(getCampaignState);
  const events = useSelector(getEventState).events.events;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [listValue, setListValue] = useState<AutomationTabValue>(
    AutomationTabValue.ACTIVE
  );
  const [showDeleteCampaign, setShowDeleteCampaign] = useState<Campaign>();

  const {
    fetchedAutomations,
    fetchedAutomationDetails,
    isLoading: isLoadingAutomations,
    isFinished: loadedAllAutomations,
    loadAutomations,
    loadMoreAutomations,
  } = useLoadAutomationItems({
    userId: accountData.uid,
    windowSize: 10,
  });

  useAsyncOnMount(async () => {
    await loadAutomations();
    // Load the template automations here
    dispatch(loadTemplateAutomations(accountData.uid));
  });

  const contains = (campaign: Campaign, query: string) => {
    return campaign.title.toLowerCase().includes(query);
  };

  const handleSearch = (text: string) => {
    setSearchTerm(text.toLowerCase());
  };

  const hideLoadMore = useMemo(
    () =>
      listValue === AutomationTabValue.TEMPLATES ||
      listValue === AutomationTabValue.OTHER,
    [listValue]
  );

  const templateAutomations = useMemo(() => {
    return campaigns.filter((campaign) =>
      isDefaultTemplateAutomations(campaign.id)
    );
  }, [campaigns]);

  const templateAutomationsDetails = useMemo(() => {
    return campaignDetails.filter((detail) =>
      templateAutomations.some((campaign) => campaign.id === detail.campaignId)
    );
  }, [campaignDetails, templateAutomations]);

  const allAutomations = useMemo(() => {
    return [...fetchedAutomations, ...templateAutomations];
  }, [fetchedAutomations, templateAutomations]);

  const allAutomationsDetails = useMemo(() => {
    return [...fetchedAutomationDetails, ...templateAutomationsDetails];
  }, [fetchedAutomationDetails, templateAutomationsDetails]);

  const filteredAutomations = useMemo(() => {
    return allAutomations.filter((campaign) => {
      const massTexts =
        allAutomationsDetails
          .find((detail) => detail.campaignId === campaign.id)
          ?.massTextDetails.map((text) => text.massText) ?? [];
      const foundEvent = events.find((event) => event.id === campaign.eventId);
      const isTemplate = isDefaultTemplateAutomations(campaign.id);
      const isOtherPreset = isDefaultPresetAutomations(campaign) && !isTemplate;

      if (listValue === AutomationTabValue.TEMPLATES) {
        return isTemplate;
      }
      if (listValue === AutomationTabValue.OTHER) {
        return isOtherPreset;
      }

      if (isDefaultPresetAutomations(campaign)) {
        return false;
      }

      if (massTexts.length === 0) {
        return listValue === AutomationTabValue.INACTIVE;
      }
      const isActive = isAutomationActive(campaign, massTexts, foundEvent);
      return listValue === AutomationTabValue.ACTIVE ? isActive : !isActive;
    });
  }, [allAutomations, allAutomationsDetails, events, listValue]);

  const isActiveInactiveTab = useMemo(
    () =>
      listValue === AutomationTabValue.ACTIVE ||
      listValue === AutomationTabValue.INACTIVE,
    [listValue]
  );

  const currentItemsToShow: Campaign[] = useMemo(() => {
    let campaignItems: Campaign[] = [...filteredAutomations].sort(
      (a: Campaign, b: Campaign) => {
        const priorityOrder = [
          SUBSCRIBED_CAMPAIGN_NAME,
          NON_SUBSCRIBER_REPLY_CAMPAIGN_NAME,
          DEFAULT_EVENT_TEXTS_CAMPAIGN_NAME,
          DEFAULT_PRODUCT_TEXTS_CAMPAIGN_NAME,
          DEFAULT_LINK_SUBMISSION_TEXTS_CAMPAIGN_NAME,
        ];
        const aIndex = priorityOrder.indexOf(a.title);
        const bIndex = priorityOrder.indexOf(b.title);
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return 0;
      }
    );
    if (searchTerm !== "") {
      campaignItems = filter(campaignItems, (campaign: Campaign) => {
        return contains(campaign, searchTerm);
      });
    }
    return campaignItems;
  }, [filteredAutomations, searchTerm]);

  const renderCampaignItem = useCallback(
    (item: Campaign) => {
      const foundCampaignDetail = allAutomationsDetails.find(
        (detail) => detail.campaignId === item.id
      );
      return (
        <div key={item.id}>
          <AutomationItemPreview
            campaignItem={item}
            campaignDetail={foundCampaignDetail}
            setShowDeleteCampaign={setShowDeleteCampaign}
          />
          {foundCampaignDetail ? <HorizontalDivider altMargin={14} /> : null}
        </div>
      );
    },
    [allAutomationsDetails]
  );

  const nonSearchEmptyView = useMemo(() => {
    const activeInactiveTabLabel =
      listValue === AutomationTabValue.ACTIVE ? "active" : "inactive";
    if (
      !loadedAllAutomations &&
      isActiveInactiveTab &&
      searchTerm.trim() === ""
    ) {
      return (
        <EmptyStateButton
          title={`No ${activeInactiveTabLabel} automations loaded yet`}
          description={`Automations load in batches. None of the automations loaded so far are ${activeInactiveTabLabel}. Load more to check older automations for this tab.`}
          icon={<Icon icon={"fa-solid:network-wired"} height={50} />}
          iconBox={80}
          btnText={isLoadingAutomations ? "Loading..." : "Load More"}
          onPress={loadMoreAutomations}
          disabled={isLoadingAutomations}
          containerStyles={{ paddingTop: 120 }}
        />
      );
    }

    return (
      <EmptyStateButton
        title={"No Automations Found"}
        description={
          "There were no automations found within this section. Make a product, event, or form to create its automation."
        }
        icon={<Icon icon={"fa-solid:network-wired"} height={50} />}
        iconBox={80}
        containerStyles={{ paddingTop: 120 }}
      />
    );
  }, [
    isActiveInactiveTab,
    isLoadingAutomations,
    listValue,
    loadMoreAutomations,
    loadedAllAutomations,
    searchTerm,
  ]);

  const renderEmptyView = useMemo(() => {
    return (
      <EmptyStateFlatlist
        searchTerm={searchTerm}
        isLoading={isLoadingAutomations}
        containerStyles={{ paddingTop: 120 }}
        nonSearchEmptyView={nonSearchEmptyView}
      />
    );
  }, [isLoadingAutomations, nonSearchEmptyView, searchTerm]);

  const isLoadingMore = useMemo(
    () => isLoadingAutomations && fetchedAutomations.length > 0,
    [fetchedAutomations.length, isLoadingAutomations]
  );

  return (
    <>
      <StandardListContainer
        searchComp={
          <div className="AlignedRowSpaced" style={{ width: "100%" }}>
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <SearchBoxContainer
                placeholder={"Search"}
                onChange={(e) => handleSearch(e.target.value)}
                containerStyles={{ marginTop: 0, width: 394 }}
              />
              <ListTabs
                tabLabels={["Active", "Inactive", "Templates", "Other"]}
                tabNumbers={[-1, -1, -1, -1]}
                selectedValue={listValue}
                onChange={(index) => setListValue(index as AutomationTabValue)}
                altColor={Colors.BLACK}
              />
            </div>
          </div>
        }
        listComp={
          <div style={{ paddingTop: 10 }}>
            <FlatlistLoadMore
              fullList={filteredAutomations}
              currentList={currentItemsToShow}
              renderItem={(item) => renderCampaignItem(item)}
              renderWhenEmpty={renderEmptyView}
              isLoadingMore={isLoadingMore}
              showLoadMore={
                searchTerm.trim() === "" &&
                !loadedAllAutomations &&
                !hideLoadMore
              }
              loadMoreOnPress={loadMoreAutomations}
            />
          </div>
        }
        isLoading={isLoadingAutomations && fetchedAutomations.length === 0}
      />
      <CampaignConfirmDeleteModal
        campaign={showDeleteCampaign}
        preventDelete={true}
        showDeleteModal={showDeleteCampaign !== undefined}
        setShowDeleteModal={() => setShowDeleteCampaign(undefined)}
      />
    </>
  );
};

export default AutomationsListPanel;
